<template>
  <div class="p-4 md:p-6 max-w-3xl mx-auto">
    <button type="button" class="text-xs font-bold text-[#0d4169] mb-4" @click="$router.push({ name: 'jobs' })">
      ← Back to jobs
    </button>

    <div v-if="loading" class="flex justify-center py-16">
      <div class="h-8 w-8 border-4 border-gray-300 border-t-[#0d4169] rounded-full animate-spin"></div>
    </div>

    <p v-else-if="error" class="text-rose-600 text-sm">{{ error }}</p>

    <div v-else-if="job" class="card-panel p-5 md:p-6 space-y-4 text-left">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <h1 class="text-2xl font-extrabold text-slate-800">{{ headingTitle }}</h1>
        <span class="inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold uppercase"
          :class="{
            'bg-slate-200 text-slate-600': displayStatus === 'expired',
            'bg-amber-100 text-amber-800': displayStatus === 'pending',
            'bg-emerald-100 text-emerald-800': displayStatus === 'published',
            'bg-rose-100 text-rose-700': displayStatus === 'rejected',
          }"
        >
          {{ displayStatus }}
        </span>
      </div>

      <div class="rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-700 space-y-3">
        <div v-for="(entry, index) in jobEntries" :key="'role-' + index" class="space-y-1">
          <p class="font-bold text-slate-800">{{ index + 1 }}. Job Title: {{ display(entry.title) }}</p>
          <p><span class="font-bold text-slate-800">Job Type:</span> {{ display(entry.job_type) }}</p>
          <p><span class="font-bold text-slate-800">Education Level:</span> {{ display(entry.education_level) }}</p>
          <p><span class="font-bold text-slate-800">Experience:</span> {{ display(entry.experience) }}</p>
          <p><span class="font-bold text-slate-800">Sex:</span> {{ display(entry.sex) }}</p>
          <p><span class="font-bold text-slate-800">No Of Employees:</span> {{ display(entry.no_of_employees) }}</p>
          <p><span class="font-bold text-slate-800">Sector:</span> {{ display(entry.sector) }}</p>
          <p><span class="font-bold text-slate-800">Salary:</span> {{ display(entry.salary) }}</p>
        </div>
        <div class="border-t border-slate-200 pt-3 space-y-1.5">
          <p v-for="row in commonRows" :key="row.label">
            <span class="font-bold text-slate-800">{{ row.label }}:</span>
            {{ row.value }}
          </p>
        </div>
      </div>

      <div>
        <p class="field-label mb-1">Description</p>
        <p class="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{{ job.description }}</p>
      </div>

      <div class="text-xs text-slate-500 space-y-1">
        <p>Employer: {{ job.employer_name || '—' }}</p>
        <p>Employer Phone: {{ job.employer_phone || '—' }}</p>
        <p>Telegram: {{ employerTgDisplay }}</p>
        <p v-if="job.phone">Job Phone: {{ job.phone }}</p>
        <p>Submitted: {{ formatDate(job.created_at) }}</p>
        <p>Applications: {{ job.applications_count }}</p>
      </div>

      <div v-if="job.status === 'pending'" class="flex flex-wrap gap-2 pt-2">
        <button type="button" class="btn-success" :disabled="busy" @click="approve">
          {{ busy ? 'Working…' : 'Approve & Publish' }}
        </button>
        <button type="button" class="btn-danger" :disabled="busy" @click="reject">
          Reject
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '../utils/api'
import { formatJobTitles, getJobDisplayStatus, getJobEntries } from '../utils/jobFields'

export default {
  name: 'JobReviewView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const job = ref(null)
    const loading = ref(true)
    const error = ref('')
    const busy = ref(false)

    function formatDate(value) {
      return value ? new Date(value).toLocaleString() : ''
    }

    function formatDeadline(value) {
      if (!value) return '—'
      return String(value).slice(0, 10)
    }

    function display(value) {
      const v = value == null ? '' : String(value).trim()
      return v || '—'
    }

    const employerTgDisplay = computed(() => {
      const j = job.value
      if (!j) return '—'
      const u = j.employer_username || j.telegram_username
      return u ? `@${String(u).replace(/^@/, '')}` : '—'
    })

    const jobEntries = computed(() => getJobEntries(job.value))
    const headingTitle = computed(() => formatJobTitles(job.value?.title))
    const displayStatus = computed(() => getJobDisplayStatus(job.value))

    const commonRows = computed(() => {
      const j = job.value
      if (!j) return []
      return [
        { label: 'Company Name', value: display(j.company_name) },
        { label: 'Job Deadline', value: formatDeadline(j.job_deadline) },
        { label: 'Work Address', value: display(j.work_address) },
        { label: 'Work Location', value: display(j.work_location) },
        { label: 'Phone Number', value: display(j.phone || j.employer_phone) },
        { label: 'Telegram Username', value: employerTgDisplay.value },
      ]
    })

    async function load() {
      loading.value = true
      error.value = ''
      try {
        const res = await apiFetch(`/jobs/${route.params.id}`)
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Job not found')
        job.value = data.job
      } catch (err) {
        error.value = err.message
      } finally {
        loading.value = false
      }
    }

    async function approve() {
      busy.value = true
      try {
        const res = await apiFetch(`/jobs/${route.params.id}/approve`, { method: 'POST' })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Approve failed')
        job.value = data.job
        router.push({ name: 'jobs', query: { status: 'published' } })
      } catch (err) {
        alert(err.message)
      } finally {
        busy.value = false
      }
    }

    async function reject() {
      if (!confirm('Reject this job?')) return
      busy.value = true
      try {
        const res = await apiFetch(`/jobs/${route.params.id}/reject`, { method: 'POST' })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Reject failed')
        job.value = data.job
        router.push({ name: 'jobs', query: { status: 'rejected' } })
      } catch (err) {
        alert(err.message)
      } finally {
        busy.value = false
      }
    }

    onMounted(load)
    return {
      job,
      loading,
      error,
      busy,
      jobEntries,
      headingTitle,
      displayStatus,
      commonRows,
      employerTgDisplay,
      display,
      formatDate,
      approve,
      reject,
    }
  },
}
</script>
